import { FormErrors } from 'Util/_HumanWritten/Errors/FormErrors';
import { checkIfInvalidName } from 'Util/StringUtils';
import { UserEntity } from 'Models/Entities';
import alertToast from 'Util/ToastifyUtils';
import { stringIsEmpty } from 'Util/TypeGuards';
import { store } from 'Models/Store';
import { doesUserExistViaEmail, doesUserExistViaMobile } from 'Services/Api/AccountService';
import { isEmail } from 'Validators/Functions/Email';
import { IsValidPhone } from 'Validators/Functions/HumanWritten/Phone';
import { appConfigStore } from 'Models/AppConfigStore';
import { IntlShape } from 'react-intl';

export function validateForm(
	user: UserEntity,
	specialState: {
		email: string;
		phone: string;
	},
	intl: IntlShape,
) {
	const { userPostcodeEnabled, userPhoneEnabled, userPhoneRequired } = appConfigStore;
	const newErrors: FormErrors<UserEntity> = {};

	if (
		(user.userDisplayName?.companyNameFieldEnabled ?? false) &&
		(user.userDisplayName?.companyNameFieldRequired ?? false) &&
		stringIsEmpty(user.companyName)
	) {
		newErrors.companyName = 'Company name required';
	}

	if (stringIsEmpty(user.firstName)) {
		newErrors.firstName = 'First name required';
	}
	if (checkIfInvalidName(user.firstName)) {
		newErrors.firstName = "Enter a valid first name (a-z, '-', no spaces)";
	}
	if (stringIsEmpty(user.lastName)) {
		newErrors.lastName = 'Last name required';
	}
	if (checkIfInvalidName(user.lastName)) {
		newErrors.lastName = "Enter a valid last name (a-z, '-', no spaces)";
	}
	if (stringIsEmpty(specialState.email)) {
		newErrors.email = 'Email required';
	} else if (!isEmail(specialState.email)) {
		newErrors.email = 'Enter a valid email address (xx@xx.xx)';
	}
	if (userPhoneEnabled && !store.isStaff && stringIsEmpty(specialState.phone) && userPhoneRequired) {
		newErrors.phone = 'Phone required';
	} else if (userPhoneEnabled && !stringIsEmpty(specialState.phone) && !IsValidPhone(specialState.phone)) {
		newErrors.phone = 'Enter a valid phone';
	}

	if (userPostcodeEnabled && stringIsEmpty(user.postcode)) {
		newErrors.postcode = `${intl.formatMessage({
			id: 'user-account__postcode',
			defaultMessage: 'Postcode',
		})} required`;
	}

	if (
		(user?.userDisplayName?.additionalInformationEnabled ?? false) &&
		(user?.userDisplayName?.additionalInformationRequired ?? false) &&
		stringIsEmpty(user.additionalInformation)
	) {
		newErrors.additionalInformation = 'Required';
	}

	return newErrors;
}

export async function isPhoneValid(orignalPhone: string, newPhone: string) {
	const { userPhoneEnabled, userPhoneRequired } = appConfigStore;
	if (!userPhoneEnabled) return true;

	// Staff are not required to enter a phone number on an account
	if (stringIsEmpty(newPhone) && store.isStaff) {
		return true;
	}
	if (!userPhoneRequired && stringIsEmpty(newPhone)) {
		return true;
	}
	if (!IsValidPhone(newPhone)) {
		alertToast('Please enter a valid mobile', 'error', 'Invalid mobile');
		return false;
	}
	if (orignalPhone !== newPhone && (await doesUserExistViaMobile(newPhone))) {
		alertToast('Mobile number is already taken', 'error', 'Invalid mobile');
		return false;
	}
	return true;
}

export async function isEmailValid(originalEmail: string, newEmail: string) {
	if (!isEmail(newEmail)) {
		alertToast('Please enter a valid email', 'error', 'Invalid email');
		return false;
	}
	if (originalEmail !== newEmail && (await doesUserExistViaEmail(newEmail))) {
		alertToast('Email address is already in use', 'error', 'Invalid email');
		return false;
	}
	return true;
}
